import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import CloseIcon from "@mui/icons-material/Close";
import "../../Font/styles/PayoutPage.css";

/* ── All Indian states + UTs ─────────────────────────── */
const INDIA_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh",
  "Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka",
  "Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram",
  "Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana",
  "Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
  "Andaman and Nicobar Islands","Chandigarh","Dadra and Nagar Haveli and Daman and Diu",
  "Delhi","Jammu and Kashmir","Ladakh","Lakshadweep","Puducherry",
];

const EMPTY_FORM = {
  label: "",
  name: "",
  phone: "",
  line1: "",
  line2: "",
  city: "",
  district: "",
  state: "",
  pincode: "",
  country: "India",
};

/* ── Cart helpers (reads localStorage) ─────────────── */
const getCart = () => {
  try {
    return JSON.parse(localStorage.getItem("cart") || "[]");
  } catch {
    return [];
  }
};

const calcSubtotal = (items) =>
  items.reduce((s, i) => s + (i.price || 0) * (i.qty || 1), 0);

const SHIPPING = 0;
const TAX_RATE = 0.18;

/* ═══════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════ */
const PayoutPage = () => {
  const { user, saveFirstAddress } = useAuth();
  const navigate = useNavigate();

  /* ── cart ─────────────────────────────────────────── */
  const [cartItems] = useState(getCart);
  const subtotal = calcSubtotal(cartItems);
  const tax = +(subtotal * TAX_RATE).toFixed(2);
  const total = subtotal + SHIPPING + tax;

  /* ── coupon ───────────────────────────────────────── */
  const [coupon, setCoupon] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponError, setCouponError] = useState("");
  const VALID_COUPON = "ramsita";
  const discount = couponApplied ? +(total * 0.1).toFixed(2) : 0;

  /* ── addresses ────────────────────────────────────── */
  /*
   * We keep a LOCAL list of addresses for this payout session.
   * It is pre-seeded with the settings address if it exists.
   * Adding more here never touches settings (except the very
   * first address when settings has none).
   */
  const settingsHasAddress =
    user?.address?.line1 && user.address.line1.trim() !== "";

  const [addresses, setAddresses] = useState(() => {
    if (settingsHasAddress) {
      return [
        {
          id: "settings",
          label: user.address.label || "Home",
          name: user.address.name || user.name || "",
          phone: user.address.phone || user.phone || "",
          line1: user.address.line1,
          line2: user.address.line2 || "",
          city: user.address.city || "",
          district: user.address.district || "",
          state: user.address.state || "",
          pincode: user.address.pincode || "",
          country: user.address.country || "India",
        },
      ];
    }
    return [];
  });

  const [selectedId, setSelectedId] = useState(
    settingsHasAddress ? "settings" : null
  );

      useEffect(() => {

        document.title =
            "Payout | Tecniqa";

    }, []);

  /* ── add-form state ───────────────────────────────── */
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  /* ── success modal ────────────────────────────────── */
  const [orderPlaced, setOrderPlaced] = useState(false);

  /* ── field change ─────────────────────────────────── */
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  /* ── validate ─────────────────────────────────────── */
  const validateForm = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (!form.phone.trim()) errs.phone = "Phone is required";
    if (!form.line1.trim()) errs.line1 = "Address line 1 is required";
    if (!form.city.trim()) errs.city = "City is required";
    if (!form.state) errs.state = "State is required";
    if (!/^\d{6}$/.test(form.pincode)) errs.pincode = "Enter valid 6-digit pincode";
    return errs;
  };

  /* ── save address from form ───────────────────────── */
  const handleSaveAddress = () => {
    const errs = validateForm();
    if (Object.keys(errs).length) {
      setFormErrors(errs);
      return;
    }

    const isFirst = addresses.length === 0; // no address in settings either

    const newAddr = {
      ...form,
      id: `addr_${Date.now()}`,
    };

    setAddresses((prev) => [...prev, newAddr]);
    setSelectedId(newAddr.id);
    setShowForm(false);
    setForm(EMPTY_FORM);
    setFormErrors({});

    /*
     * Only save to settings if user had NO address at all.
     * After that first save, payout addresses stay local.
     */
    if (isFirst) {
      saveFirstAddress(newAddr);
    }
  };

  /* ── coupon apply ─────────────────────────────────── */
  const handleApplyCoupon = () => {
    setCouponError("");
    if (coupon.trim().toUpperCase() === VALID_COUPON) {
      setCouponApplied(true);
    } else {
      setCouponError("Invalid coupon code");
    }
  };

  /* ── place order ──────────────────────────────────── */
  const handlePlaceOrder = () => {
    if (!selectedId) return;
    setOrderPlaced(true);
    localStorage.removeItem("cart");
    window.dispatchEvent(new Event("cartUpdated"));
  };

  /* ── success screen ───────────────────────────────── */
  if (orderPlaced) {
    return (
      <div className="po-success-screen">
        <div className="po-success-card">
          <div className="po-success-icon">
            <CheckCircleIcon style={{ fontSize: 72 }} />
          </div>
          <h2 className="po-success-title">Order Placed!</h2>
          <p className="po-success-sub">
            Thank you for your purchase. You'll receive a confirmation email shortly.
          </p>
          <div className="po-success-actions">
            <button
              className="po-btn-primary"
              onClick={() => navigate("/")}
            >
              Continue Shopping
            </button>
            <button
              className="po-btn-ghost"
              onClick={() => navigate("/dashboard/orders")}
            >
              View Orders
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="po-root">
      <div className="po-header">
        <h1 className="po-header-title">Checkout</h1>
        <p className="po-header-sub">Complete your order</p>
      </div>

      <div className="po-layout">
        {/* ══════════════════════════════
            LEFT — DELIVERY
        ══════════════════════════════ */}
        <div className="po-left">
          {/* Section heading */}
          <div className="po-section-head">
            <LocationOnIcon className="po-section-icon" />
            <span className="po-section-title">Delivery Address</span>
          </div>

          {/* Saved address cards */}
          {addresses.map((addr) => (
            <div
              key={addr.id}
              className={`po-addr-card ${
                selectedId === addr.id ? "po-addr-card--selected" : ""
              }`}
              onClick={() => setSelectedId(addr.id)}
            >
              <div className="po-addr-radio">
                {selectedId === addr.id ? (
                  <CheckCircleIcon className="po-radio-checked" />
                ) : (
                  <RadioButtonUncheckedIcon className="po-radio-unchecked" />
                )}
              </div>
              <div className="po-addr-body">
                <div className="po-addr-top">
                  {addr.label && (
                    <span className="po-addr-label">{addr.label}</span>
                  )}
                  <span className="po-addr-name">{addr.name}</span>
                  {addr.phone && (
                    <span className="po-addr-phone">{addr.phone}</span>
                  )}
                </div>
                <div className="po-addr-text">
                  {[addr.line1, addr.line2, addr.city, addr.district, addr.state, addr.pincode, addr.country]
                    .filter(Boolean)
                    .join(", ")}
                </div>
              </div>
            </div>
          ))}

          {/* ADD NEW ADDRESS — inline expandable form */}
          {!showForm ? (
            <button
              className="po-add-btn"
              onClick={() => setShowForm(true)}
            >
              <AddIcon style={{ fontSize: 18 }} />
              Add New Address
            </button>
          ) : (
            <div className="po-form-card">
              <div className="po-form-head">
                <span className="po-form-title">New Delivery Address</span>
                <button
                  className="po-form-close"
                  onClick={() => {
                    setShowForm(false);
                    setForm(EMPTY_FORM);
                    setFormErrors({});
                  }}
                >
                  <CloseIcon style={{ fontSize: 18 }} />
                </button>
              </div>

              <div className="po-form-grid">
                {/* Label */}
                <div className="po-form-field po-form-field--full">
                  <label className="po-form-label">Address Label</label>
                  <input
                    className="po-form-input"
                    name="label"
                    value={form.label}
                    onChange={handleFormChange}
                    placeholder="e.g. Home, Office, Warehouse"
                  />
                </div>

                {/* Name */}
                <div className="po-form-field">
                  <label className="po-form-label">
                    Full Name <span className="po-req">*</span>
                  </label>
                  <input
                    className={`po-form-input ${formErrors.name ? "po-form-input--err" : ""}`}
                    name="name"
                    value={form.name}
                    onChange={handleFormChange}
                    placeholder="Recipient name"
                  />
                  {formErrors.name && (
                    <span className="po-form-err">{formErrors.name}</span>
                  )}
                </div>

                {/* Phone */}
                <div className="po-form-field">
                  <label className="po-form-label">
                    Phone <span className="po-req">*</span>
                  </label>
                  <input
                    className={`po-form-input ${formErrors.phone ? "po-form-input--err" : ""}`}
                    name="phone"
                    value={form.phone}
                    onChange={handleFormChange}
                    placeholder="+91 XXXXX XXXXX"
                    type="tel"
                  />
                  {formErrors.phone && (
                    <span className="po-form-err">{formErrors.phone}</span>
                  )}
                </div>

                {/* Line 1 */}
                <div className="po-form-field po-form-field--full">
                  <label className="po-form-label">
                    Address Line 1 <span className="po-req">*</span>
                  </label>
                  <input
                    className={`po-form-input ${formErrors.line1 ? "po-form-input--err" : ""}`}
                    name="line1"
                    value={form.line1}
                    onChange={handleFormChange}
                    placeholder="House no., Building, Street"
                  />
                  {formErrors.line1 && (
                    <span className="po-form-err">{formErrors.line1}</span>
                  )}
                </div>

                {/* Line 2 */}
                <div className="po-form-field po-form-field--full">
                  <label className="po-form-label">Address Line 2</label>
                  <input
                    className="po-form-input"
                    name="line2"
                    value={form.line2}
                    onChange={handleFormChange}
                    placeholder="Apartment, Area, Landmark (optional)"
                  />
                </div>

                {/* City */}
                <div className="po-form-field">
                  <label className="po-form-label">
                    City <span className="po-req">*</span>
                  </label>
                  <input
                    className={`po-form-input ${formErrors.city ? "po-form-input--err" : ""}`}
                    name="city"
                    value={form.city}
                    onChange={handleFormChange}
                    placeholder="City"
                  />
                  {formErrors.city && (
                    <span className="po-form-err">{formErrors.city}</span>
                  )}
                </div>

                {/* District */}
                <div className="po-form-field">
                  <label className="po-form-label">District</label>
                  <input
                    className="po-form-input"
                    name="district"
                    value={form.district}
                    onChange={handleFormChange}
                    placeholder="District"
                  />
                </div>

                {/* State */}
                <div className="po-form-field">
                  <label className="po-form-label">
                    State <span className="po-req">*</span>
                  </label>
                  <select
                    className={`po-form-input po-form-select ${formErrors.state ? "po-form-input--err" : ""}`}
                    name="state"
                    value={form.state}
                    onChange={handleFormChange}
                  >
                    <option value="">Select State / UT</option>
                    {INDIA_STATES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  {formErrors.state && (
                    <span className="po-form-err">{formErrors.state}</span>
                  )}
                </div>

                {/* Pincode */}
                <div className="po-form-field">
                  <label className="po-form-label">
                    Pincode <span className="po-req">*</span>
                  </label>
                  <input
                    className={`po-form-input ${formErrors.pincode ? "po-form-input--err" : ""}`}
                    name="pincode"
                    value={form.pincode}
                    onChange={handleFormChange}
                    placeholder="6-digit pincode"
                    maxLength={6}
                  />
                  {formErrors.pincode && (
                    <span className="po-form-err">{formErrors.pincode}</span>
                  )}
                </div>

                {/* Country */}
                <div className="po-form-field">
                  <label className="po-form-label">Country</label>
                  <select
                    className="po-form-input po-form-select"
                    name="country"
                    value={form.country}
                    onChange={handleFormChange}
                  >
                    <option value="India">India</option>
                    <option value="Nepal">Nepal</option>
                    <option value="Bangladesh">Bangladesh</option>
                    <option value="Sri Lanka">Sri Lanka</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="po-form-actions">
                <button className="po-btn-primary" onClick={handleSaveAddress}>
                  Save Address
                </button>
                <button
                  className="po-btn-ghost"
                  onClick={() => {
                    setShowForm(false);
                    setForm(EMPTY_FORM);
                    setFormErrors({});
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ══════════════════════════════
            RIGHT — ORDER SUMMARY
        ══════════════════════════════ */}
        <div className="po-right">
          <div className="po-summary-card">
            <div className="po-summary-title">Order Summary</div>

            {/* Items */}
            <div className="po-items">
              {cartItems.length === 0 ? (
                <div className="po-empty-cart">Your cart is empty</div>
              ) : (
                cartItems.map((item, idx) => (
                  <div className="po-item" key={idx}>
                    {item.image && (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="po-item-img"
                      />
                    )}
                    <div className="po-item-info">
                      <div className="po-item-name">{item.title || item.name}</div>
                      {item.brand && (
                        <div className="po-item-brand">{item.brand}</div>
                      )}
                      <div className="po-item-qty">Qty: {item.qty || 1}</div>
                    </div>
                    <div className="po-item-price">
                      ₹{((item.price || 0) * (item.qty || 1)).toLocaleString("en-IN")}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="po-divider" />

            {/* Coupon */}
            <div className="po-coupon-section">
              <div className="po-coupon-head">
                <LocalOfferIcon style={{ fontSize: 16 }} />
                <span>Coupon Code</span>
              </div>
              {couponApplied ? (
                <div className="po-coupon-applied">
                  <CheckCircleIcon style={{ fontSize: 16 }} />
                  <span>HVAC10 applied — 10% off</span>
                  <button
                    className="po-coupon-remove"
                    onClick={() => {
                      setCouponApplied(false);
                      setCoupon("");
                    }}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="po-coupon-row">
                  <input
                    className="po-coupon-input"
                    placeholder="Enter coupon code"
                    value={coupon}
                    onChange={(e) => {
                      setCoupon(e.target.value);
                      setCouponError("");
                    }}
                    onKeyDown={(e) => e.key === "Enter" && handleApplyCoupon()}
                  />
                  <button
                    className="po-coupon-btn"
                    onClick={handleApplyCoupon}
                  >
                    Apply
                  </button>
                </div>
              )}
              {couponError && (
                <div className="po-coupon-error">{couponError}</div>
              )}
            </div>

            <div className="po-divider" />

            {/* Price breakdown */}
            <div className="po-price-rows">
              <div className="po-price-row">
                <span>Subtotal</span>
                <span>₹{subtotal.toLocaleString("en-IN")}</span>
              </div>
              <div className="po-price-row">
                <span>Shipping</span>
                <span className="po-free">Free</span>
              </div>
              <div className="po-price-row">
                <span>GST (18%)</span>
                <span>₹{tax.toLocaleString("en-IN")}</span>
              </div>
              {couponApplied && (
                <div className="po-price-row po-price-row--discount">
                  <span>Discount (10%)</span>
                  <span>−₹{discount.toLocaleString("en-IN")}</span>
                </div>
              )}
            </div>

            <div className="po-total-row">
              <span>Total</span>
              <span>₹{(total - discount).toLocaleString("en-IN")}</span>
            </div>

            {/* Place order button */}
            <button
              className={`po-place-btn ${
                !selectedId ? "po-place-btn--disabled" : ""
              }`}
              onClick={handlePlaceOrder}
              disabled={!selectedId}
            >
              {!selectedId
                ? "Select a Delivery Address"
                : `Pay ₹${(total - discount).toLocaleString("en-IN")}`}
            </button>

            {!selectedId && addresses.length === 0 && (
              <p className="po-addr-hint">
                Add a delivery address above to continue
              </p>
            )}
            {!selectedId && addresses.length > 0 && (
              <p className="po-addr-hint">
                Select a delivery address to continue
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayoutPage;